"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Light = void 0;
const LightForm_1 = require("./components/LightForm");
const ObjectInfo_1 = require("../object-info/ObjectInfo");
const UiElements_1 = require("../ui/UiElements");
const ModelTransform_1 = require("../model-transform/ModelTransform");
const ViewerOptions_1 = require("../../objects/ViewerOptions");
const Language_1 = require("../language/Language");
const DEVIATION = 0.01;
class Light {
    constructor(webgl) {
        this.lightForms = [];
        this.lightFunctions = {
            addLight: () => this.add("DIRECTIONAL" /* DIRECTIONAL */)
        };
        this.updateIntensity = (intensity) => {
            var _a, _b;
            const object = (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.selected;
            if (!object) {
                return;
            }
            if (object.intensity !== undefined && Math.abs(object.intensity - intensity) >= DEVIATION) {
                object.intensity = intensity;
                (_b = this.webgl) === null || _b === void 0 ? void 0 : _b.renderOnce();
            }
        };
        this.updateColor = (color) => {
            var _a, _b;
            const object = (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.selected;
            if (!object) {
                return;
            }
            const colorHexValue = parseInt(color.substring(1), 16);
            if (object.color !== undefined && object.color.getHex() !== colorHexValue) {
                const color = colorHexValue ? colorHexValue : 0xffffff;
                object.color.setHex(color);
                (_b = this.webgl) === null || _b === void 0 ? void 0 : _b.renderOnce();
            }
        };
        this.webgl = webgl;
        this.language = new Language_1.Language(ViewerOptions_1.ViewerOptions.language);
        const parentEl = document.getElementById('lightsContainer');
        if (!parentEl) {
            throw new Error("Parent element (light) is undefined");
        }
        // Add light button
        this.addLightBtn = document.createElement('button');
        this.addLightBtn.setAttribute('data-i18n-key', 'ADD_NEW');
        this.addLightBtn.type = 'button';
        parentEl.appendChild(this.addLightBtn);
        // Light list
        this.lightList = new UiElements_1.DivEl().setId('lightList').domEl;
        parentEl.appendChild(this.lightList);
        this.addEvents();
        this.lightClickedSub = PubSub.subscribe("object-info" /* OBJECT_INFO */, (_, data) => this.lightClicked(data));
        this.lightsDataSub = PubSub.subscribe("lights-list" /* LIGHTS_LIST */, () => this.createLightsList());
    }
    lightClicked(light) {
        var _a, _b;
        if (!light) {
            return;
        }
        (_a = document.querySelector('.accord.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
        const accordEl = document.querySelector('.accord[data-id="' + light.uuid + '"]');
        accordEl === null || accordEl === void 0 ? void 0 : accordEl.classList.add('active');
        const form = document.querySelector('form[data-for-id="' + light.uuid + '"]');
        // Hide previous open form
        (_b = document.querySelector('#lightList > form:not([hidden])')) === null || _b === void 0 ? void 0 : _b.setAttribute('hidden', '');
        // Show current
        form === null || form === void 0 ? void 0 : form.removeAttribute('hidden');
    }
    dispose() {
        this.removeEvents();
        PubSub.unsubscribe(this.lightsDataSub);
        PubSub.unsubscribe(this.lightClickedSub);
    }
    add(lightType) {
        var _a;
        (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.lights.add(lightType, null, true);
        this.redrawLightList();
    }
    redrawLightList() {
        this.lightList.innerHTML = '';
        this.createLightsList();
    }
    remove(light) {
        var _a;
        (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.lights.remove(light);
    }
    hideLightHelpers() {
        this.hideOrShowLightHelpers(false);
    }
    showLightHelpers() {
        this.hideOrShowLightHelpers(true);
    }
    hideOrShowLightHelpers(status) {
        var _a, _b;
        for (const lightHelper of ((_b = (_a = this.webgl) === null || _a === void 0 ? void 0 : _a.lights) === null || _b === void 0 ? void 0 : _b.lightHelpers) || []) {
            lightHelper.visible = status;
        }
    }
    addEvents() {
        this.addLightBtn.addEventListener('click', this.lightFunctions.addLight);
    }
    removeEvents() {
        this.addLightBtn.removeEventListener('click', this.lightFunctions.addLight);
        for (const lightForm of this.lightForms) {
            lightForm.intensityRange.oninput = null;
            lightForm.colorSelectorEl.onchange = null;
            lightForm.typeSelectEl.onchange = null;
        }
    }
    createLightsList() {
        const lights = this.webgl.lights.lightHelpers.map(h => h.light);
        if (lights.length === 0) {
            this.createNoLightsMessage();
            return;
        }
        let counter = 0;
        for (const light of lights) {
            counter++;
            const accordEl = this.createAccordElement(light.uuid, light.type, counter);
            const lightForm = new LightForm_1.LightForm(this.lightList, light);
            this.lightList.append(accordEl.domEl, lightForm.formEl.domEl);
            this.lightForms.push(lightForm);
        }
        this.addLightFormEvents();
        // Regenerate translation texts, otherwise the newly created elements will have no text
        this.language.updateTranslatableElements();
    }
    createNoLightsMessage() {
        const spanEl = new UiElements_1.SpanEl().addAttribute('data-i18n-key', 'NO_LIGHTS_MESSAGE');
        this.lightList.appendChild(spanEl.domEl);
        this.language.updateTranslatableElements();
    }
    createAccordElement(lightUuid, lightType, counter) {
        const lightTypeSpanEl = new UiElements_1.SpanEl().setInnerHTML(counter + '. ' + lightType);
        const optionsDivEl = new UiElements_1.DivEl()
            .setClass('light-options')
            .addChild(new UiElements_1.ButtonEl()
            .setClass('remove')
            .addAttribute('data-i18n-attr', 'data-title=REMOVE_LIGHT')
            .addChild(new UiElements_1.IconEl('delete')));
        return new UiElements_1.DivEl()
            .setClass('accord')
            .addAttribute('data-id', lightUuid + '')
            .addChild(lightTypeSpanEl)
            .addChild(optionsDivEl);
    }
    addLightFormEvents() {
        const accords = this.lightList.querySelectorAll('.accord');
        for (const accord of accords) {
            const form = accord.nextElementSibling;
            if (!form) {
                continue;
            }
            form.onmousedown = () => {
                if (!this.webgl.selected) {
                    this.webgl.lights.selectLight(form.dataset.forId);
                }
            };
            form.onsubmit = (e) => {
                e.preventDefault();
            };
            const lightId = accord.dataset.id || '0';
            new ModelTransform_1.ModelTransform(this.webgl, form);
            new ObjectInfo_1.ObjectInfo(this.webgl, 'light', form, lightId);
            accord.onclick = () => {
                var _a, _b, _c, _d, _e;
                // Hide previous
                (_a = document.querySelector('.accord.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
                (_b = document.querySelector('#lightList > form:not([hidden])')) === null || _b === void 0 ? void 0 : _b.setAttribute('hidden', '');
                // Show current
                form === null || form === void 0 ? void 0 : form.removeAttribute('hidden');
                accord.classList.add('active');
                for (const lightHelper of ((_d = (_c = this.webgl) === null || _c === void 0 ? void 0 : _c.lights) === null || _d === void 0 ? void 0 : _d.lightHelpers) || []) {
                    if (lightHelper.light.uuid === accord.dataset.id) {
                        PubSub.publish("light-selected" /* LIGHT_SELECTED */, lightHelper);
                    }
                }
                (_e = this.webgl) === null || _e === void 0 ? void 0 : _e.renderOnce();
            };
            // Register delete button
            const removeBtn = accord.querySelector('button.remove');
            removeBtn.onclick = () => this.removeLightById(accord.dataset.id);
        }
        // Register form events
        for (const lightForm of this.lightForms) {
            lightForm.intensityRange.oninput = () => {
                var _a;
                const value = parseInt(((_a = lightForm === null || lightForm === void 0 ? void 0 : lightForm.intensityRange) === null || _a === void 0 ? void 0 : _a.value) || '0');
                this.updateIntensity(value);
                lightForm.intensityRange.style.backgroundSize = value + '% 100%';
                lightForm.intensityNumberSpan.innerText = ' ' + value + '';
            };
            lightForm.colorSelectorEl.onchange = () => {
                var _a;
                this.updateColor((_a = lightForm.colorSelectorEl) === null || _a === void 0 ? void 0 : _a.value);
            };
            lightForm.typeSelectEl.onchange = () => {
                this.changeLightType(lightForm);
            };
        }
    }
    removeLightById(lightUuid) {
        if (!lightUuid) {
            return;
        }
        this.webgl.lights.removeByUuid(lightUuid);
        this.redrawLightList();
    }
    changeLightType(lightForm) {
        var _a, _b;
        const type = (_a = lightForm.typeSelectEl) === null || _a === void 0 ? void 0 : _a.value;
        const lightUuid = (_b = lightForm.formEl) === null || _b === void 0 ? void 0 : _b.domEl.dataset.forId;
        let lightType = "DIRECTIONAL" /* DIRECTIONAL */;
        switch (type) {
            case 'PointLight':
                lightType = "POINT" /* POINT */;
                break;
            case 'SpotLight':
                lightType = "SPOT" /* SPOT */;
                break;
            default:
                break;
        }
        const lightHelper = this.webgl.lights.changeType(lightUuid, lightType);
        this.redrawLightList();
        const newLightUuid = lightHelper === null || lightHelper === void 0 ? void 0 : lightHelper.light.uuid;
        const newAccord = document.querySelector('.accord[data-id="' + newLightUuid + '"]');
        newAccord === null || newAccord === void 0 ? void 0 : newAccord.click();
    }
}
exports.Light = Light;
